import { motion } from 'framer-motion';
import { useState, useRef } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    year: '',
    make: '',
    model: '',
    comments: '',
  });
  const [fileName, setFileName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });
      if (fileName) {
        formBody.append('photo_upload', 'Uncollectable');
      }

      const response = await fetch('https://readdy.ai/api/form/d6acaf5biueuoqm1sds0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ first_name: '', last_name: '', phone: '', email: '', year: '', make: '', model: '', comments: '' });
        setFileName('');
        if (fileInputRef.current) fileInputRef.current.value = '';
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFileName(e.target.files[0].name);
    } else {
      setFileName('');
    }
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-[1200px] mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="font-montserrat font-bold text-4xl md:text-5xl text-text-dark mb-4">
            Ready to Restore Your Vehicle?
          </h2>
          <p className="font-inter text-lg text-text-muted max-w-2xl mx-auto">
            Get a free estimate today. No hidden fees, no surprises.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="bg-bg-soft rounded-2xl p-8">
              <h3 className="font-montserrat font-bold text-2xl text-text-dark mb-6">
                Get Free Estimate
              </h3>
              <form onSubmit={handleSubmit} data-readdy-form id="estimate-form">
                <div className="space-y-4">
                  {/* First & Last Name */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="first_name" className="block font-inter font-medium text-text-dark mb-2 text-sm">
                        First Name *
                      </label>
                      <input
                        type="text"
                        id="first_name"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all font-inter text-sm"
                        placeholder="First name"
                      />
                    </div>
                    <div>
                      <label htmlFor="last_name" className="block font-inter font-medium text-text-dark mb-2 text-sm">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        id="last_name"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all font-inter text-sm"
                        placeholder="Last name"
                      />
                    </div>
                  </div>

                  {/* Phone & Email */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="phone" className="block font-inter font-medium text-text-dark mb-2 text-sm">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all font-inter text-sm"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block font-inter font-medium text-text-dark mb-2 text-sm">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all font-inter text-sm"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  {/* Year, Make, Model */}
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label htmlFor="year" className="block font-inter font-medium text-text-dark mb-2 text-sm">
                        Year *
                      </label>
                      <input
                        type="text"
                        id="year"
                        name="year"
                        value={formData.year}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all font-inter text-sm"
                        placeholder="2024"
                        maxLength={4}
                      />
                    </div>
                    <div>
                      <label htmlFor="make" className="block font-inter font-medium text-text-dark mb-2 text-sm">
                        Make *
                      </label>
                      <input
                        type="text"
                        id="make"
                        name="make"
                        value={formData.make}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all font-inter text-sm"
                        placeholder="Toyota"
                      />
                    </div>
                    <div>
                      <label htmlFor="model" className="block font-inter font-medium text-text-dark mb-2 text-sm">
                        Model *
                      </label>
                      <input
                        type="text"
                        id="model"
                        name="model"
                        value={formData.model}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all font-inter text-sm"
                        placeholder="Camry"
                      />
                    </div>
                  </div>

                  {/* Comments */}
                  <div>
                    <label htmlFor="comments" className="block font-inter font-medium text-text-dark mb-2 text-sm">
                      Comments
                    </label>
                    <textarea
                      id="comments"
                      name="comments"
                      value={formData.comments}
                      onChange={handleChange}
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all font-inter text-sm resize-none"
                      placeholder="Describe the damage, location on vehicle, how it happened..."
                    ></textarea>
                    <p className="text-xs text-text-muted mt-1">{formData.comments.length}/500 characters</p>
                  </div>

                  {/* Photo Upload */}
                  <div>
                    <label className="block font-inter font-medium text-text-dark mb-2 text-sm">
                      Upload Photo (optional)
                    </label>
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full border-2 border-dashed border-gray-300 rounded-lg px-4 py-6 flex flex-col items-center justify-center cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-all"
                    >
                      <i className="ri-camera-line text-3xl text-text-muted mb-2"></i>
                      {fileName ? (
                        <p className="font-inter text-sm text-text-dark font-medium">{fileName}</p>
                      ) : (
                        <>
                          <p className="font-inter text-sm text-text-muted">Click to upload a photo of the damage</p>
                          <p className="font-inter text-xs text-text-muted mt-1">JPG, PNG up to 10MB</p>
                        </>
                      )}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      name="photo_upload"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-primary hover:bg-[#e67028] text-white font-montserrat font-semibold px-6 py-4 rounded-full transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? 'Sending...' : 'Get Free Estimate'}
                  </button>

                  {submitStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg font-inter text-sm flex items-center gap-2">
                      <i className="ri-checkbox-circle-fill text-green-600"></i>
                      Thank you! We&apos;ll review your info and contact you shortly.
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg font-inter text-sm flex items-center gap-2">
                      <i className="ri-error-warning-fill text-red-600"></i>
                      Something went wrong. Please try again or call us directly.
                    </div>
                  )}
                </div>
              </form>
            </div>
          </motion.div>

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            {/* Call Us */}
            <div className="bg-primary/5 rounded-2xl p-8">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 flex items-center justify-center bg-primary rounded-full flex-shrink-0">
                  <i className="ri-phone-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="font-montserrat font-bold text-xl text-text-dark mb-2">
                    Call Us
                  </h3>
                  <a
                    href="tel:660-342-6164"
                    className="font-inter text-lg text-primary hover:text-[#e67028] transition-colors cursor-pointer"
                  >
                    (660) 342-6164
                  </a>
                  <p className="font-inter text-text-muted mt-2">
                    Mon–Fri: 8AM–5PM
                  </p>
                </div>
              </div>
            </div>

            {/* Email Us */}
            <div className="bg-secondary/5 rounded-2xl p-8">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 flex items-center justify-center bg-secondary rounded-full flex-shrink-0">
                  <i className="ri-mail-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="font-montserrat font-bold text-xl text-text-dark mb-2">
                    Email Us
                  </h3>
                  <a
                    href="mailto:info@kcpdr.com"
                    className="font-inter text-lg text-secondary hover:text-secondary-dark transition-colors cursor-pointer"
                  >
                    info@kcpdr.com
                  </a>
                  <p className="font-inter text-text-muted mt-2">
                    We'll respond within 24 hours
                  </p>
                </div>
              </div>
            </div>

            {/* Visit Us */}
            <div className="bg-primary/5 rounded-2xl p-8">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 flex items-center justify-center bg-primary rounded-full flex-shrink-0">
                  <i className="ri-map-pin-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="font-montserrat font-bold text-xl text-text-dark mb-2">
                    Visit Us
                  </h3>
                  <p className="font-inter text-text-muted">
                    9240 Northwest 63rd Street<br />
                    Parkville, MO 64152
                  </p>
                  <p className="font-inter text-sm text-primary mt-2 font-medium">
                    By appointment only
                  </p>
                </div>
              </div>
            </div>

            {/* FAQ Quick Links */}
            <div className="bg-bg-soft rounded-2xl p-8">
              <h3 className="font-montserrat font-bold text-xl text-text-dark mb-4">
                Common Questions
              </h3>
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-secondary mt-1"></i>
                  <p className="font-inter text-text-muted text-sm">
                    <strong className="text-text-dark">Pricing:</strong> Free estimates, competitive rates
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-secondary mt-1"></i>
                  <p className="font-inter text-text-muted text-sm">
                    <strong className="text-text-dark">Insurance:</strong> We work with all major carriers
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-secondary mt-1"></i>
                  <p className="font-inter text-text-muted text-sm">
                    <strong className="text-text-dark">Time:</strong> Most repairs done same day
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-secondary mt-1"></i>
                  <p className="font-inter text-text-muted text-sm">
                    <strong className="text-text-dark">Qualifies:</strong> Intact paint, no cracks or chips
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
